import { useState, useRef } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { ImageCropper } from '@/components/ImageCropper';

/**
 * GM Comics Create page - form to create a new newspaper comic.
 */
export default function Create() {
    // Form fields
    const [title, setTitle] = useState('');
    const [artistName, setArtistName] = useState('');
    const [artistUrl, setArtistUrl] = useState('');
    const [thumbnail, setThumbnail] = useState<File | null>(null);
    const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
    const [images, setImages] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [altTexts, setAltTexts] = useState<string[]>([]);
    const [isPublished, setIsPublished] = useState(false);

    // Image cropper state
    const [imageToCrop, setImageToCrop] = useState<string | null>(null);
    const thumbnailInputRef = useRef<HTMLInputElement>(null);
    const imagesInputRef = useRef<HTMLInputElement>(null);

    // Form state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    /**
     * Handle thumbnail file selection - opens the cropper modal.
     */
    const handleThumbnailSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Read the file and open the cropper
        const reader = new FileReader();
        reader.onload = () => {
            setImageToCrop(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    /**
     * Handle crop completion - receives the cropped image blob.
     */
    const handleCropComplete = (croppedBlob: Blob) => {
        // Convert blob to File for FormData
        const croppedFile = new File([croppedBlob], 'thumbnail.jpg', { type: 'image/jpeg' });
        setThumbnail(croppedFile);

        // Create preview URL
        setThumbnailPreview(URL.createObjectURL(croppedBlob));

        // Close cropper
        setImageToCrop(null);

        // Reset file input
        if (thumbnailInputRef.current) {
            thumbnailInputRef.current.value = '';
        }
    };

    /**
     * Handle crop cancellation.
     */
    const handleCropCancel = () => {
        setImageToCrop(null);
        if (thumbnailInputRef.current) {
            thumbnailInputRef.current.value = '';
        }
    };

    /**
     * Remove the selected thumbnail.
     */
    const handleRemoveThumbnail = () => {
        setThumbnail(null);
        setThumbnailPreview(null);
        if (thumbnailInputRef.current) {
            thumbnailInputRef.current.value = '';
        }
    };

    /**
     * Handle comic images selection.
     */
    const handleImagesSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        // Add new files to existing images
        setImages((prev) => [...prev, ...files]);

        // Add empty alt texts for new files
        setAltTexts((prev) => [...prev, ...files.map(() => '')]);

        // Create previews for new files
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onload = () => {
                setImagePreviews((prev) => [...prev, reader.result as string]);
            };
            reader.readAsDataURL(file);
        });

        // Reset file input to allow selecting same files again
        if (imagesInputRef.current) {
            imagesInputRef.current.value = '';
        }
    };

    /**
     * Remove an image from the list.
     */
    const handleRemoveImage = (index: number) => {
        setImages((prev) => prev.filter((_, i) => i !== index));
        setImagePreviews((prev) => prev.filter((_, i) => i !== index));
        setAltTexts((prev) => prev.filter((_, i) => i !== index));
    };

    /**
     * Move an image up in the order.
     */
    const handleMoveUp = (index: number) => {
        if (index === 0) return;
        setImages((prev) => {
            const newImages = [...prev];
            [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
            return newImages;
        });
        setImagePreviews((prev) => {
            const newPreviews = [...prev];
            [newPreviews[index - 1], newPreviews[index]] = [newPreviews[index], newPreviews[index - 1]];
            return newPreviews;
        });
        setAltTexts((prev) => {
            const newAltTexts = [...prev];
            [newAltTexts[index - 1], newAltTexts[index]] = [newAltTexts[index], newAltTexts[index - 1]];
            return newAltTexts;
        });
    };

    /**
     * Move an image down in the order.
     */
    const handleMoveDown = (index: number) => {
        if (index === images.length - 1) return;
        setImages((prev) => {
            const newImages = [...prev];
            [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
            return newImages;
        });
        setImagePreviews((prev) => {
            const newPreviews = [...prev];
            [newPreviews[index], newPreviews[index + 1]] = [newPreviews[index + 1], newPreviews[index]];
            return newPreviews;
        });
        setAltTexts((prev) => {
            const newAltTexts = [...prev];
            [newAltTexts[index], newAltTexts[index + 1]] = [newAltTexts[index + 1], newAltTexts[index]];
            return newAltTexts;
        });
    };

    /**
     * Update alt text for a specific image.
     */
    const handleAltTextChange = (index: number, value: string) => {
        setAltTexts((prev) => {
            const newAltTexts = [...prev];
            newAltTexts[index] = value;
            return newAltTexts;
        });
    };

    /**
     * Handle form submission.
     */
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        // Use FormData for file upload
        const formData = new FormData();
        if (title) formData.append('title', title);
        formData.append('artist_name', artistName);
        if (artistUrl) formData.append('artist_url', artistUrl);
        if (thumbnail) formData.append('thumbnail', thumbnail);
        images.forEach((image) => {
            formData.append('images[]', image);
        });
        altTexts.forEach((altText) => {
            formData.append('alt_texts[]', altText);
        });
        formData.append('is_published', isPublished ? '1' : '0');

        router.post('/gm/newspaper/comics', formData, {
            forceFormData: true,
            onError: (errs) => {
                setErrors(errs);
                setIsSubmitting(false);
            },
            onSuccess: () => setIsSubmitting(false),
        });
    };

    return (
        <GmLayout title="Create Feature" activePath="/gm/newspaper">
            <div className="max-w-4xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">🎨 Create Feature</h1>
                    <Link href="/gm/newspaper/comics" className="text-gray-400 hover:text-white transition-colors">
                        ← Back to Features
                    </Link>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Info Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-white mb-4">Feature Information</h2>

                        {/* Title (optional) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Title (optional)
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                placeholder="Leave blank for untitled Feature"
                            />
                            {errors.title && <p className="mt-1 text-sm text-red-400">{errors.title}</p>}
                        </div>

                        {/* Artist Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Artist Name <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                value={artistName}
                                onChange={(e) => setArtistName(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                placeholder="Artist's name or handle"
                                required
                            />
                            {errors.artist_name && <p className="mt-1 text-sm text-red-400">{errors.artist_name}</p>}
                        </div>

                        {/* Artist URL */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Artist URL (optional)
                            </label>
                            <input
                                type="url"
                                value={artistUrl}
                                onChange={(e) => setArtistUrl(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                placeholder="https://artist-website.com"
                            />
                            {errors.artist_url && <p className="mt-1 text-sm text-red-400">{errors.artist_url}</p>}
                        </div>

                        {/* Thumbnail */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Thumbnail <span className="text-red-400">*</span>
                            </label>
                            <p className="text-xs text-gray-500 mb-2">
                                Square image (1:1 aspect ratio) for the Features grid
                            </p>
                            {thumbnailPreview && (
                                <div className="mb-3">
                                    <img
                                        src={thumbnailPreview}
                                        alt="Thumbnail preview"
                                        className="w-32 h-32 object-cover rounded border border-slate-600"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleRemoveThumbnail}
                                        className="mt-2 text-xs text-red-400 hover:text-red-300"
                                    >
                                        Remove thumbnail
                                    </button>
                                </div>
                            )}
                            <input
                                ref={thumbnailInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleThumbnailSelect}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white file:mr-4 file:py-1 file:px-4 file:rounded file:border-0 file:bg-purple-600 file:text-white file:cursor-pointer"
                            />
                            {errors.thumbnail && <p className="mt-1 text-sm text-red-400">{errors.thumbnail}</p>}
                        </div>
                    </div>

                    {/* Comic Images Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-white mb-4">Feature Images</h2>
                        <p className="text-sm text-gray-400 mb-4">
                            Upload one or more images for this Feature. For multi-panel Features, upload each panel as a separate image.
                        </p>

                        {/* Image Previews */}
                        {imagePreviews.length > 0 && (
                            <div className="space-y-4 mb-4">
                                {imagePreviews.map((preview, index) => (
                                    <div key={index} className="bg-slate-700 rounded overflow-hidden p-3">
                                        <div className="flex gap-4">
                                            <div className="relative flex-shrink-0 w-32">
                                                <img
                                                    src={preview}
                                                    alt={`Image ${index + 1}`}
                                                    className="w-32 h-32 object-cover rounded"
                                                />
                                                <div className="absolute top-1 left-1 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                                    {index + 1}
                                                </div>
                                            </div>
                                            <div className="flex-1 space-y-2">
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-1">
                                                        Alt Text (Image Description)
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={altTexts[index] || ''}
                                                        onChange={(e) => handleAltTextChange(index, e.target.value)}
                                                        placeholder="Describe this image for accessibility"
                                                        className="w-full px-2 py-1 text-sm bg-slate-600 border border-slate-500 rounded text-white placeholder-gray-400"
                                                    />
                                                </div>
                                                <div className="flex gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMoveUp(index)}
                                                        disabled={index === 0}
                                                        className="px-2 py-1 bg-slate-600 hover:bg-slate-500 text-white rounded text-xs disabled:opacity-50"
                                                    >
                                                        ↑ Move Up
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMoveDown(index)}
                                                        disabled={index === imagePreviews.length - 1}
                                                        className="px-2 py-1 bg-slate-600 hover:bg-slate-500 text-white rounded text-xs disabled:opacity-50"
                                                    >
                                                        ↓ Move Down
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveImage(index)}
                                                        className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                                                    >
                                                        ✕ Remove
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <input
                            ref={imagesInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImagesSelect}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white file:mr-4 file:py-1 file:px-4 file:rounded file:border-0 file:bg-purple-600 file:text-white file:cursor-pointer"
                        />
                        {errors.images && <p className="mt-1 text-sm text-red-400">{errors.images}</p>}
                        {errors['images.0'] && <p className="mt-1 text-sm text-red-400">{errors['images.0']}</p>}
                    </div>

                    {/* Publish Status */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                        <div className="flex items-center">
                            <label className="flex items-center gap-2 text-sm text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={isPublished}
                                    onChange={(e) => setIsPublished(e.target.checked)}
                                    className="rounded bg-slate-700 border-slate-600 text-purple-600"
                                />
                                Publish immediately (visible to all users)
                            </label>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            Draft Features are only visible to GMs. You can publish later from the edit page.
                        </p>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex gap-4">
                        <button
                            type="submit"
                            disabled={isSubmitting || !thumbnail || images.length === 0}
                            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors disabled:opacity-50"
                        >
                            {isSubmitting ? 'Creating...' : 'Create Feature'}
                        </button>
                        <Link
                            href="/gm/newspaper/comics"
                            className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>

            {/* Image Cropper Modal for Thumbnail */}
            {imageToCrop && (
                <ImageCropper
                    imageSrc={imageToCrop}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                    targetWidth={400}
                    targetHeight={400}
                />
            )}
        </GmLayout>
    );
}

